import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';
import { readToolResultText } from '../../../contracts/toolResult';
import { artifactOpenLabel } from '../../../data/projects/artifactOpenLabel';
import type { Artifact } from '../../../data/projects/artifacts/state';
import { friendlyToolError } from '../../../data/projects/components/projectChatMessage.helpers';
import { readConnectRequired } from '../../../data/projects/integrations/connectRequired';
import { readDesktopRequired } from '../../../data/projects/integrations/desktopRequired';
import { toBlockStatus } from '../../../data/projects/timeline/genericToolUseStatus';
import { DESKTOP_APP_DOWNLOAD_URL } from '../../../data/workflows/lib/preset-meta';
import type { ConnectRequiredBlockProps } from '../components/ConnectRequiredBlock';
import { DesktopRequiredBlock } from '../components/DesktopRequiredBlock';
import { InlineToolCallRow } from './InlineToolCallRow';

export interface GenericToolUseRowProps {
  item: ToolUseItem;
  onOpenArtifact: (artifact: Artifact) => void;
  items?: ChatItem[];
  /** Derives the row's artifact; the app binds the slide-builder flag and session tabs. */
  resolveArtifact: (item: ToolUseItem) => Artifact | undefined;
  /** The connect card, which the app binds to the integrations connect buttons. */
  renderConnectRequired: (props: ConnectRequiredBlockProps) => ReactNode;
}

/**
 * The default presentation for a tool use that has no bespoke adapter:
 * a clinician CTA when the failure is connect-an-app or install-desktop,
 * nothing when the agent already has the result, otherwise the generic
 * tool-call row plus an artifact "Open" affordance when the result produced one.
 *
 * Shared by the per-item renderer fallback and the expanded view of a
 * grouped tool-call batch so a single call looks identical whether it
 * stands alone or sits inside a group.
 */
export const GenericToolUseRow = ({
  item,
  onOpenArtifact,
  items,
  resolveArtifact,
  renderConnectRequired,
}: GenericToolUseRowProps): ReactNode => {
  const intl = useIntl();
  const status = toBlockStatus(item.status);

  if (status === 'error') {
    const errorText = readToolResultText(item);
    // Connect-an-app and scheduled desktop misses are the only generic
    // failures the clinician can act on. Agent-facing results (empty
    // search, rate limit, adaptor routing) stay off the transcript.
    const connect = readConnectRequired(item);
    if (connect) {
      return renderConnectRequired({
        payload: connect,
        error: friendlyToolError(errorText),
        toolCallId: item.id,
        items,
      });
    }
    const desktopRequired = readDesktopRequired(item);
    if (desktopRequired) {
      if (desktopRequired.trigger === 'on_demand') return null;
      return (
        <DesktopRequiredBlock
          payload={desktopRequired}
          downloadHref={DESKTOP_APP_DOWNLOAD_URL}
        />
      );
    }
    return null;
  }

  const artifact = resolveArtifact(item);
  const canOpen = status === 'success' && !!artifact;

  return (
    <InlineToolCallRow
      toolName={item.name}
      toolInput={item.input}
      status={status}
      toolCallDisplay={item.toolCallDisplay}
      onOpen={canOpen ? () => onOpenArtifact(artifact) : undefined}
      openLabel={canOpen ? artifactOpenLabel(intl, artifact) : undefined}
      openTestId={canOpen ? `open-artifact-${artifact.type}` : undefined}
    />
  );
};
